import {
  Api, AuthenticationOptions, getDefaultClient, types as api, types,
} from '@mesa-labs/mesa-api';
import {
  AxiosError, AxiosInstance, AxiosResponse,
} from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import jwtDecode from 'jwt-decode';
import { FacilitatorRedactedVendorApi } from '@mesa-labs/mesa-api/dist/apis/facilitatorRedactedVendor';
import { FacilitatorRedactedInvoiceApi } from '@mesa-labs/mesa-api/dist/apis/facilitatorRedactedInvoice';
import { FacilitatorRedactedExternalVendorApi } from '@mesa-labs/mesa-api/dist/apis/facilitatorRedactedExternalVendor';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';

import { updateAccessToken } from '../slices/auth';
import { RootState } from '../store';
import { exportResource, getAccessToken } from './utils';

export type TokenParam = {
  readonly accessToken: string;
};

type ApiCtor<T extends Api> = new (baseURL: string, options?: AuthenticationOptions, client?: AxiosInstance) => T;

const isTokenInvalidOrExpired = (token: string): boolean => {
  const payload = jwtDecode<string | { exp: number }>(token);
  if (!payload) {
    return true;
  }

  if (typeof payload === 'string' || !payload.exp) {
    return true;
  }

  return (Date.now() / 1000) > payload.exp;
};

export const useApi = <T extends Api>(baseURL: string, Ctor: ApiCtor<T>, dispatch: ThunkDispatch<any, any, any>, options?: AuthenticationOptions) => {
  const client = getDefaultClient(baseURL);

  client.interceptors.request.use((config) => {
    config.headers.set('mode', 'cors');
    config.headers.set('cache', 'no-cache');
    config.headers.set('credentials', 'same-origin');
    config.headers.set('Accept', 'application/json');
    config.headers.set('Content-Type', 'application/json');
    if (options) {
      config.headers.set('Authorization', `Bearer ${options.token}`);
    }
    return config;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => ({
      ...response,
      data: response.status === 404 ? undefined : response.data,
    }),
    async (err?: AxiosError) => {
      if (options?.token) {
        if (isTokenInvalidOrExpired(options.token)) {
          return dispatch(updateAccessToken(undefined));
        }
      }

      throw api.ApiError.fromAxiosError(err);
    },
  );

  // Don't pass options as they won't take effect due to the custom client
  return new Ctor(baseURL, undefined, client);
};

export const useFacilitatorRedactedVendorApi = (dispatch: ThunkDispatch<any, any, any>, options?: AuthenticationOptions) => useApi(CONFIG.api.vendorUrl, FacilitatorRedactedVendorApi, dispatch, options);
export const useFacilitatorRedactedInvoiceApi = (dispatch: ThunkDispatch<any, any, any>, options?: AuthenticationOptions) => useApi(CONFIG.api.invoiceUrl, FacilitatorRedactedInvoiceApi, dispatch, options);
export const useFacilitatorRedactedExternalVendorApi = (dispatch: ThunkDispatch<any, any, any>, options?: AuthenticationOptions) => useApi(CONFIG.api.externalVendorUrl, FacilitatorRedactedExternalVendorApi, dispatch, options);
export const useVendorApi = (dispatch: ThunkDispatch<any, any, any>, options?: AuthenticationOptions) => useApi(CONFIG.api.vendorUrl, VendorApi, dispatch, options);

export const facilitatorsApi = createApi({
  reducerPath: 'facilitatorsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
  }),
  tagTypes: ['Vendors', 'Vendor', 'VendorInvoices', 'Clients', 'Client', 'ClientVendors', 'ClientDivision', 'ExternalVendors', 'ExternalVendorFilters', 'InvoiceApprovals', 'InvoiceApprovalReasonSummary'],
  endpoints: (build) => ({
    authorizeFacilitator:
      build.query<api.AuthorizeFacilitatorResponse, api.AuthorizeFacilitatorRequest & { partnerId: api.Partners }>({
        async queryFn({ partnerId, ...request }, { dispatch }) {
          const vendorApi = useFacilitatorRedactedVendorApi(dispatch /* no token */);
          const data = await vendorApi.authorizeFacilitator(partnerId, request);
          return { data };
        },
      }),
    getAllVendorsAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedVendorResponse>, api.FacilitatorVendorFilterParams & { partnerId: api.Partners; } & TokenParam>({
        async queryFn({ partnerId, ...params }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const vendorApi = useFacilitatorRedactedVendorApi(dispatch, { token: accessToken });
          const data = await vendorApi.getAllVendorsAsFacilitator(partnerId, params);
          return { data };
        },
        providesTags: ['Vendors'],
      }),
    getVendorAsFacilitator:
      build.query<api.FacilitatorRedactedVendorAggregateResponse | undefined, { partnerId: api.Partners; vendorId: types.UUID } & TokenParam>({
        async queryFn({ partnerId, vendorId }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const vendorApi = useFacilitatorRedactedVendorApi(dispatch, { token: accessToken });
          const data = await vendorApi.getVendorAsFacilitator(partnerId, vendorId);
          return { data };
        },
        providesTags: (result, _error, { vendorId }) => (result
          ? [{ type: 'Vendor', vendorId, ...result }]
          : ['Vendor']),
      }),
    getAllInvoicesAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedInvoiceResponse>, Omit<api.FacilitatorInvoiceFilterParams, 'approvalReasons'> & { partnerId: api.Partners; } & TokenParam>({
        async queryFn({ partnerId, ...params }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const invoiceApi = useFacilitatorRedactedInvoiceApi(dispatch, { token: accessToken });
          const data = await invoiceApi.getAllInvoicesAsFacilitator(partnerId, params);
          return { data };
        },
        providesTags: (result, _error, { vendorId }) => (result
          ? [{ type: 'VendorInvoices', vendorId, ...result }]
          : ['VendorInvoices']),
      }),
    exportAllInvoicesAsFacilitator:
      build.query<BlobPart, Omit<api.FacilitatorInvoiceFilterParams, 'approvalReasons'> & { partnerId: api.Partners; }>({
        async queryFn({ partnerId, ...params }, { getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const data = await exportResource(new URL(`${CONFIG.api.invoiceUrl}/facilitators/partners/${partnerId}/invoices/csv/export`), params, accessToken);
          return { data };
        },
      }),
    getAllExternalClientsAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedExternalClientResponse>, api.FacilitatorExternalClientFilterParams & { partnerId: api.Partners; } & TokenParam>({
        async queryFn({
          partnerId, search, ...params
        }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getAllExternalClientsAsFacilitator(partnerId, { search, ...params });
          return { data };
        },
        providesTags: ['Clients'],
      }),
    exportAllExternalClientsAsFacilitator:
      build.query<BlobPart, api.FacilitatorExternalClientFilterParams & { partnerId: api.Partners; }>({
        async queryFn({ partnerId, search, ...params }, { getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const data = await exportResource(new URL(`${CONFIG.api.externalVendorUrl}/facilitators/partners/${partnerId}/external-clients/csv/export`), params, accessToken);
          return { data };
        },
      }),
    getExternalClientAsFacilitator:
      build.query<api.FacilitatorRedactedExternalClientResponse | undefined, { partnerId: api.Partners; externalClientId: string; } & TokenParam>({
        async queryFn({ partnerId, externalClientId }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getExternalClientAsFacilitator(partnerId, externalClientId);
          return { data };
        },
        providesTags: (result) => (result
          ? [{ type: 'Client', ...result }]
          : ['Client']),
      }),
    getExternalClientVendorsAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedExternalClientVendorResponse>, api.ExternalClientVendorFilterParams & { partnerId: api.Partners; externalClientId: string; } & TokenParam>({
        async queryFn({
          partnerId, externalClientId, ...params
        }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getExternalClientVendorsAsFacilitator(partnerId, externalClientId, params);
          return { data };
        },
        providesTags: (result, _error, { partnerId, externalClientId }) => (result
          ? [{
            type: 'ClientVendors', partnerId, externalClientId, ...result,
          }]
          : ['ClientVendors']),
      }),
    exportAllExternalClientVendorsAsFacilitator:
      build.query<BlobPart, api.ExternalClientVendorFilterParams & { partnerId: api.Partners; externalClientId: string; }>({
        async queryFn({ partnerId, externalClientId, ...params }, { getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const data = await exportResource(new URL(`${CONFIG.api.externalVendorUrl}/facilitators/partners/${partnerId}/external-clients/${externalClientId}/external-vendors/csv/export`), params, accessToken);
          return { data };
        },
      }),
    getExternalClientDivisionAsFacilitator:
      build.query<api.FacilitatorRedactedExternalClientDivisionResponse, { partnerId: api.Partners; division: api.ExternalClientDivision } & TokenParam>({
        async queryFn({
          partnerId, division,
        }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getExternalClientDivisionAsFacilitator(partnerId, division);
          return { data };
        },
        providesTags: (result, _error) => (result
          ? [{
            type: 'ClientDivision', ...result,
          }]
          : ['ClientDivision']),
      }),
    getAllExternalVendorsAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedExternalVendorResponse>, api.FacilitatorExternalVendorFilterParams & { partnerId: api.Partners } & TokenParam>({
        async queryFn({ partnerId, ...params }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getAllExternalVendorsAsFacilitator(partnerId, params);
          return { data };
        },
        providesTags: ['ExternalVendors'],
      }),
    exportAllExternalVendorsAsFacilitator:
      build.query<BlobPart, api.FacilitatorExternalVendorFilterParams & { partnerId: api.Partners }>({
        async queryFn({ partnerId, ...params }, { getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const data = await exportResource(new URL(`${CONFIG.api.externalVendorUrl}/facilitators/partners/${partnerId}/external-vendors/csv/export`), params, accessToken);
          return { data };
        },
      }),
    getExternalVendorAsFacilitator:
      build.query<api.FacilitatorRedactedExternalVendorResponse | undefined, { partnerId: api.Partners, externalVendorId: string } & TokenParam>({
        async queryFn({ partnerId, externalVendorId }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getExternalVendorAsFacilitator(partnerId, externalVendorId);
          return { data };
        },
        providesTags: (result, _error) => (result
          ? [{
            type: 'ExternalVendors', ...result,
          }]
          : ['ExternalVendors']),
      }),
    getExternalVendorFiltersAsFacilitator:
      build.query<api.ExternalVendorFiltersResponse, { partnerId: api.Partners } & TokenParam>({
        async queryFn({ partnerId }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const externalVendorApi = useFacilitatorRedactedExternalVendorApi(dispatch, { token: accessToken });
          const data = await externalVendorApi.getExternalVendorFiltersAsFacilitator(partnerId);
          return { data };
        },
        providesTags: ['ExternalVendorFilters'],
      }),
    getInvoiceApprovalReasonSummaryAsFacilitator:
      build.query<api.InvoiceApprovalReasonSummaryResponse, api.FacilitatorInvoiceApprovalReasonSummaryFilterParams & { partnerId: api.Partners; } & TokenParam>({
        async queryFn({ partnerId, ...params }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const invoiceApi = useFacilitatorRedactedInvoiceApi(dispatch, { token: accessToken });
          const data = await invoiceApi.getInvoiceApprovalReasonSummaryAsFacilitator(partnerId, params);
          return { data };
        },
        providesTags: (result, _error, { approvalReasons }) => (result
          ? [{ type: 'InvoiceApprovalReasonSummary', approvalReasons, ...result }]
          : ['InvoiceApprovalReasonSummary']),
      }),
    getAllInvoiceApprovalsAsFacilitator:
      build.query<api.IPagedResults<api.FacilitatorRedactedInvoiceResponse>, Omit<api.FacilitatorInvoiceFilterParams, 'vendorId'> & { partnerId: api.Partners; } & TokenParam>({
        async queryFn({ partnerId, ...params }, { dispatch, getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const invoiceApi = useFacilitatorRedactedInvoiceApi(dispatch, { token: accessToken });
          const data = await invoiceApi.getAllInvoicesAsFacilitator(partnerId, params);
          return { data };
        },
        providesTags: (result, _error, { approvalReasons }) => (result
          ? [{ type: 'InvoiceApprovals', approvalReasons, ...result }]
          : ['InvoiceApprovals']),
      }),
    exportAllInvoiceApprovalsAsFacilitator:
      build.query<BlobPart, Omit<api.FacilitatorInvoiceFilterParams, 'vendorId'> & { partnerId: api.Partners; }>({
        async queryFn({ partnerId, ...params }, { getState }) {
          const accessToken = await getAccessToken(getState as () => RootState);
          const data = await exportResource(new URL(`${CONFIG.api.invoiceUrl}/facilitators/partners/${partnerId}/invoices/csv/export`), params, accessToken);
          return { data };
        },
      }),
    impersonateFacilitator: build.mutation<void, { partnerId: api.Partners }>({
      async queryFn({ partnerId }, { dispatch, getState }) {
        const accessToken = await getAccessToken(getState as () => RootState);
        const vendorApi = useVendorApi(dispatch, { token: accessToken });
        const data = await vendorApi.impersonateFacilitator(partnerId);
        return { data };
      },
    }),
    endFacilitatorImpersonation: build.mutation<void, { partnerId: api.Partners }>({
      async queryFn({ partnerId }, { dispatch, getState }) {
        const accessToken = await getAccessToken(getState as () => RootState);
        const vendorApi = useVendorApi(dispatch, { token: accessToken });
        const data = await vendorApi.endFacilitatorImpersonation(partnerId);
        return { data };
      },
    }),
    endVendorImpersonation: build.mutation<void, { id: string }>({
      async queryFn({ id }, { dispatch, getState }) {
        const accessToken = await getAccessToken(getState as () => RootState);
        const vendorApi = useVendorApi(dispatch, { token: accessToken });
        const data = await vendorApi.endVendorImpersonation(id);
        return { data };
      },
    }),
  }),
});

export const {
  useAuthorizeFacilitatorQuery,
  useGetAllVendorsAsFacilitatorQuery,
  useGetVendorAsFacilitatorQuery,
  useGetAllInvoicesAsFacilitatorQuery,
  useExportAllInvoicesAsFacilitatorQuery,
  useLazyExportAllInvoicesAsFacilitatorQuery,
  useGetAllExternalClientsAsFacilitatorQuery,
  useGetExternalClientAsFacilitatorQuery,
  useExportAllExternalClientsAsFacilitatorQuery,
  useLazyExportAllExternalClientsAsFacilitatorQuery,
  useGetExternalClientVendorsAsFacilitatorQuery,
  useExportAllExternalClientVendorsAsFacilitatorQuery,
  useLazyExportAllExternalClientVendorsAsFacilitatorQuery,
  useGetExternalClientDivisionAsFacilitatorQuery,
  useGetAllExternalVendorsAsFacilitatorQuery,
  useExportAllExternalVendorsAsFacilitatorQuery,
  useLazyExportAllExternalVendorsAsFacilitatorQuery,
  useGetExternalVendorAsFacilitatorQuery,
  useGetExternalVendorFiltersAsFacilitatorQuery,
  useGetInvoiceApprovalReasonSummaryAsFacilitatorQuery,
  useGetAllInvoiceApprovalsAsFacilitatorQuery,
  useExportAllInvoiceApprovalsAsFacilitatorQuery,
  useLazyExportAllInvoiceApprovalsAsFacilitatorQuery,
  useImpersonateFacilitatorMutation,
  useEndFacilitatorImpersonationMutation,
  useEndVendorImpersonationMutation,
} = facilitatorsApi;

export const asExportFilter = <T>(filter: T): T => ({
  ...filter,
  page: 1,
  limit: Number.MAX_SAFE_INTEGER,
});
